import React, { useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { withCapture, _ } from 'utils'
import ConfirmModal from 'components/ConfirmModal'
import FullPageLoading from 'components/FullPageLoading'
import { useEventSlug } from 'hooks'
import { Tag } from 'components/TagRow'
import TextField from 'features/forms/components/TextField'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import ColorField from 'features/forms/components/ColorField'
import { CreateTag } from '../generated/graphql'

const S = {
    Wrapper: styled.div`
        margin: 0 -10px;
        width: calc(100% + 20px);
    `
}

interface TagProps {
  close: () => void
  onSubmitted?: () => void
  minOrder?: number
  tag?: Tag
  categoryId?: string
  createTag?:(data: CreateTag) => Promise<void>
  updateTag?:({ text, color, categoryId }, id) => Promise<void>
}

interface TagModalProps {
  close: () => void;
}

const TagForm = () => (
    <S.Wrapper>
        <TextField name="name" label={_('show.form.tag_name')} isRequired />
        <ColorField
            name="color"
            label={_('show.form.color')}
            isRequired
            defaultValue="#ffffff"
        />
    </S.Wrapper>
)

const Modal = ({ close }: TagModalProps) => {
    const { submitForm, isValid } = useFormikContext()
    return (
        <>
            <ConfirmModal
                close={close}
                title={_('show.create_tag_dialog.title')}
                confirmDescription={<TagForm />}
                confirmText={_('show.create_tag_button.title')}
                disabledConfirm={!isValid}
                onBeforeConfirm={submitForm}
                variantConfirm="success"
            />
        </>
    )
}

const TagModal = ({
    close, onSubmitted = () => {}, minOrder, tag, categoryId,
    createTag = async () => {}, updateTag = async () => {}
}: TagProps) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const [isUploading, setUploading] = useState(false)

    const handleSubmit = withCapture(async ({ name, color }) => {
        setUploading(true)
        let response
        if (!tag) {
            response = await createTag({
                text: name, order: minOrder - 1000, color, eventSlug, categoryId
            })
        } else {
            response = await updateTag({ text: name, color, categoryId: tag.categoryId }, tag.id)
        }
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))

            onSubmitted()
            return true
        }
        setUploading(false)
        dispatch(showErrorNotification(response.errors))
        return false
    })

    return (
        <Formik
            initialValues={{ name: tag?.text, color: tag?.color }}
            validateOnMount
            validate={(values) => {
                const errors: { name?: string, color?: string } = {}
                if (!values.name) {
                    errors.name = _('show.validation.tag_name.required')
                }
                if (!values.color) {
                    errors.color = _('show.validation.tag_color.required')
                }
                return errors
            }}
            onSubmit={handleSubmit}
        >
            <>
                <Modal close={close} />
                {isUploading && <FullPageLoading isAbsolute fullWidth />}
            </>
        </Formik>
    )
}

export default TagModal
